import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie,
  ScatterChart, Scatter, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell
} from 'recharts';
import {
  TrendingUp,
  Target,
  Zap,
  Activity,
  BarChart3,
  PieChartIcon,
  Crosshair,
  MapPin
} from 'lucide-react';
import { ShotData } from '../types';
import { cn } from '@/lib/utils';

interface ShotAnalyticsViewProps {
  shots: ShotData[];
  className?: string;
  timeRange?: 'day' | 'week' | 'month' | 'all';
  onTimeRangeChange?: (range: 'day' | 'week' | 'month' | 'all') => void;
}

export const ShotAnalyticsView: React.FC<ShotAnalyticsViewProps> = ({
  shots,
  className,
  timeRange = 'all',
  onTimeRangeChange
}) => {
  // Calculate analytics data
  const analytics = useMemo(() => {
    if (!shots || shots.length === 0) {
      return {
        performanceTrend: [],
        shotDistribution: [],
        qualityBreakdown: [],
        powerVsDistance: [],
        sweetSpotAccuracy: [],
        shotTypeAnalysis: [],
        progressionData: [],
        heatmapData: []
      };
    }

    // Performance trend over time
    const performanceTrend = shots.map((shot, index) => ({
      shot: index + 1,
      distance: shot.distance || 0,
      power: (shot.powerTransfer || 0) * 100,
      quality: shot.quality === 'excellent' ? 100 :
               shot.quality === 'good' ? 75 :
               shot.quality === 'fair' ? 50 : 25,
      exitVelocity: shot.exitVelocity || 0
    }));

    // Shot type distribution
    const shotTypes = shots.reduce((acc, shot) => {
      const type = shot.shotType || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const shotDistribution = Object.entries(shotTypes).map(([name, value]) => ({
      name,
      value,
      percentage: (value / shots.length) * 100
    }));

    // Quality breakdown
    const qualityCounts = shots.reduce((acc, shot) => {
      const quality = shot.quality || 'unknown';
      acc[quality] = (acc[quality] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const qualityBreakdown = Object.entries(qualityCounts).map(([quality, count]) => ({
      quality,
      count,
      percentage: (count / shots.length) * 100
    }));

    // Power vs Distance correlation
    const powerVsDistance = shots.map(shot => ({
      power: (shot.powerTransfer || 0) * 100,
      distance: shot.distance || 0,
      quality: shot.quality || 'unknown'
    }));

    // Sweet spot accuracy
    const sweetSpotData = shots.reduce((acc, shot) => {
      const distance = shot.sweetSpotDistance || 0;
      const range = distance < 5 ? 'Perfect (<5mm)' :
                   distance < 10 ? 'Excellent (5-10mm)' :
                   distance < 20 ? 'Good (10-20mm)' : 'Poor (>20mm)';
      acc[range] = (acc[range] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const sweetSpotAccuracy = Object.entries(sweetSpotData).map(([range, count]) => ({
      range,
      count,
      percentage: (count / shots.length) * 100
    }));

    // Shot type performance analysis
    const shotTypeAnalysis = Object.keys(shotTypes).map(type => {
      const typeShots = shots.filter(s => s.shotType === type);
      return {
        shotType: type,
        avgDistance: typeShots.reduce((acc, s) => acc + (s.distance || 0), 0) / typeShots.length,
        avgPower: typeShots.reduce((acc, s) => acc + (s.powerTransfer || 0), 0) / typeShots.length * 100,
        avgSpeed: typeShots.reduce((acc, s) => acc + (s.exitVelocity || 0), 0) / typeShots.length,
        count: typeShots.length
      };
    });

    // Progression data (moving averages)
    const windowSize = 5;
    const progressionData = shots.map((_, index) => {
      const start = Math.max(0, index - windowSize + 1);
      const window = shots.slice(start, index + 1);
      return {
        shot: index + 1,
        avgDistance: window.reduce((acc, s) => acc + (s.distance || 0), 0) / window.length,
        avgPower: window.reduce((acc, s) => acc + (s.powerTransfer || 0), 0) / window.length * 100,
        avgQuality: window.filter(s => s.quality === 'excellent' || s.quality === 'good').length / window.length * 100
      };
    });

    // Heatmap data for shot placement
    const heatmapData = shots.map(shot => ({
      x: shot.impactLocationX || 0,
      y: shot.impactLocationY || 0,
      value: shot.distance || 0,
      quality: shot.quality || 'unknown'
    }));

    return {
      performanceTrend,
      shotDistribution,
      qualityBreakdown,
      powerVsDistance,
      sweetSpotAccuracy,
      shotTypeAnalysis,
      progressionData,
      heatmapData
    };
  }, [shots]);

  const COLORS = {
    EXCELLENT: '#10b981',
    GOOD: '#3b82f6',
    AVERAGE: '#f59e0b',
    POOR: '#ef4444',
    UNKNOWN: '#6b7280'
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header with time range selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Shot Analytics</h2>
          <p className="text-muted-foreground">Comprehensive analysis of {shots.length} shots</p>
        </div>
        <Select value={timeRange} onValueChange={onTimeRangeChange as any}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Today</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="distribution">Distribution</TabsTrigger>
          <TabsTrigger value="accuracy">Accuracy</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Performance Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Performance Trend
                </CardTitle>
                <CardDescription>Distance and power over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics.performanceTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="shot" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="distance" stroke="#3b82f6" name="Distance (m)" />
                    <Line yAxisId="right" type="monotone" dataKey="power" stroke="#10b981" name="Power (%)" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Quality Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" />
                  Quality Breakdown
                </CardTitle>
                <CardDescription>Shot quality distribution</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics.qualityBreakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ quality, percentage }) => `${quality}: ${percentage.toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {analytics.qualityBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[entry.quality as keyof typeof COLORS]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Power vs Distance Scatter */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Power vs Distance
                </CardTitle>
                <CardDescription>Correlation between power transfer and distance</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="power" name="Power" unit="%" />
                    <YAxis dataKey="distance" name="Distance" unit="m" />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Scatter name="Shots" data={analytics.powerVsDistance} fill="#3b82f6">
                      {analytics.powerVsDistance.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[entry.quality as keyof typeof COLORS]} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Shot Type Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Shot Type Performance
                </CardTitle>
                <CardDescription>Average metrics by shot type</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.shotTypeAnalysis}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="shotType" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="avgDistance" fill="#3b82f6" name="Avg Distance (m)" />
                    <Bar dataKey="avgPower" fill="#10b981" name="Avg Power (%)" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Distribution Tab */}
        <TabsContent value="distribution" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Shot Type Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" />
                  Shot Type Distribution
                </CardTitle>
                <CardDescription>Breakdown by shot type</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics.shotDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percentage }) => `${name}: ${percentage.toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {analytics.shotDistribution.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 50%)`} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Field Heatmap Placeholder */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Shot Placement Heatmap
                </CardTitle>
                <CardDescription>Distribution of shot placements on field</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] relative bg-gradient-to-b from-green-50 to-green-100 rounded-lg">
                  {/* Render heatmap visualization here */}
                  <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <Crosshair className="h-12 w-12 mx-auto mb-2" />
                      <p>Field heatmap visualization</p>
                      <p className="text-sm">Shows shot placement density</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Accuracy Tab */}
        <TabsContent value="accuracy" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Sweet Spot Accuracy
              </CardTitle>
              <CardDescription>Distribution of impact distances from sweet spot</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.sweetSpotAccuracy}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="percentage" fill="#3b82f6" name="Percentage (%)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Performance Progression
              </CardTitle>
              <CardDescription>Moving averages showing improvement over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={analytics.progressionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="shot" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="avgDistance" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="Avg Distance (m)" />
                  <Area type="monotone" dataKey="avgPower" stackId="2" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Avg Power (%)" />
                  <Area type="monotone" dataKey="avgQuality" stackId="3" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} name="Quality Score (%)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};